import React from 'react';
import {
  Shield,
  User,
  Home,
  LogOut,
  Database,
  Building2,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { UserRole } from '../types';

interface Props {
  onOpenSupabaseModal: () => void;
}

export const Navbar: React.FC<Props> = ({ onOpenSupabaseModal }) => {
  const { currentUser, logout, selectedRole, isSupabaseConnected } = useApp();

  const getRoleBadge = (role: UserRole) => {
    switch (role) {
      case 'resident':
        return { label: 'ลูกบ้าน', color: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'staff':
        return { label: 'พนักงาน / รปภ.', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
      case 'admin':
        return { label: 'นิติบุคคล / แอดมิน', color: 'bg-purple-50 text-purple-700 border-purple-200' };
    }
  };

  const badge = getRoleBadge(selectedRole);

  return (
    <header id="main-navbar" className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-xs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Brand Logo & Title */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-700 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-blue-500/20">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg text-blue-900 tracking-tight">นราวดี อิลิแกนซ์</span>
              <span className="hidden sm:inline-flex text-[11px] px-2 py-0.5 rounded-full font-medium bg-blue-100/70 text-blue-800 border border-blue-200/60">
                Elegance Village
              </span>
            </div>
            <p className="text-xs text-slate-500 hidden sm:block">ระบบจัดการหมู่บ้านจัดสรรที่ครบวงจร</p>
          </div>
        </div>

        {/* Center / Right controls */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Supabase Status Button */}
          <button
            id="navbar-supabase-btn"
            onClick={onOpenSupabaseModal}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 transition-all ${
              isSupabaseConnected
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300'
            }`}
            title="คลิกเพื่อตั้งค่าหรือดูสถานะ Supabase"
          >
            <Database className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Supabase:</span>
            {isSupabaseConnected ? (
              <span className="flex items-center gap-1 text-emerald-700">
                <CheckCircle2 className="w-3 h-3" /> ออนไลน์
              </span>
            ) : (
              <span className="text-slate-500">จำลองข้อมูล</span>
            )}
          </button>
          {/* User Profile Card */}
          {currentUser && (
            <div className="flex items-center gap-3 pl-2 sm:border-l sm:border-slate-200">
              <div className="text-right hidden sm:block">
                <div className="text-xs font-semibold text-slate-800 flex items-center justify-end gap-1.5">
                  {currentUser.full_name}
                  {currentUser.house_number && (
                    <span className="text-blue-600 text-[11px] font-mono">({currentUser.house_number})</span>
                  )}
                </div>
                <span className={`inline-block text-[10px] px-2 py-0.2 rounded border font-medium ${badge.color}`}>
                  {badge.label}
                </span>
              </div>

              <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700 font-bold text-sm">
                {currentUser.full_name ? currentUser.full_name.charAt(0) : <User className="w-4 h-4" />}
              </div>

              <button
                id="btn-logout"
                onClick={logout}
                title="ออกจากระบบ"
                className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
